import {
	ATHENA_BILLING_PROVIDER_CONFIG_INVALID,
	AthenaBillingProviderError,
} from "../../../errors.ts";
import { normalizeBillingMoney } from "../../../safety/money.ts";
import type {
	AthenaBillingCatalogConfig,
	BillingPrice,
	BillingProduct,
} from "../../../types.ts";

export interface NormalizedAthenaBillingCatalog {
	prices: readonly BillingPrice[] | null;
	products: readonly BillingProduct[] | null;
}

function nonEmptyId(value: unknown, label: string): string {
	if (typeof value !== "string" || value.trim().length === 0) {
		throw new AthenaBillingProviderError({
			code: ATHENA_BILLING_PROVIDER_CONFIG_INVALID,
			message: `billing.catalog ${label} requires a non-empty id.`,
		});
	}
	return value.trim();
}

export function normalizeAthenaBillingCatalog(
	catalog?: AthenaBillingCatalogConfig,
): NormalizedAthenaBillingCatalog | undefined {
	if (catalog == null) {
		return undefined;
	}
	const productsConfigured = Array.isArray(catalog.products);
	const pricesConfigured = Array.isArray(catalog.prices);
	if (!productsConfigured && !pricesConfigured) {
		return undefined;
	}

	const products: BillingProduct[] = [];
	const productIds = new Set<string>();
	if (productsConfigured) {
		for (const entry of catalog.products ?? []) {
			const id = nonEmptyId(entry.id, "product");
			if (productIds.has(id)) {
				throw new AthenaBillingProviderError({
					code: ATHENA_BILLING_PROVIDER_CONFIG_INVALID,
					message: `billing.catalog product id ${id} is duplicated.`,
				});
			}
			if (typeof entry.name !== "string" || entry.name.trim().length === 0) {
				throw new AthenaBillingProviderError({
					code: ATHENA_BILLING_PROVIDER_CONFIG_INVALID,
					message: `billing.catalog product ${id} requires a name.`,
				});
			}
			productIds.add(id);
			products.push({
				description: entry.description,
				id,
				metadata: entry.metadata ?? {},
				name: entry.name.trim(),
				raw: entry,
			});
		}
	}

	const prices: BillingPrice[] = [];
	const priceIds = new Set<string>();
	if (pricesConfigured) {
		for (const entry of catalog.prices ?? []) {
			const id = nonEmptyId(entry.id, "price");
			if (priceIds.has(id)) {
				throw new AthenaBillingProviderError({
					code: ATHENA_BILLING_PROVIDER_CONFIG_INVALID,
					message: `billing.catalog price id ${id} is duplicated.`,
				});
			}
			const productId = nonEmptyId(entry.productId, "price.productId");
			if (productsConfigured && !productIds.has(productId)) {
				throw new AthenaBillingProviderError({
					code: ATHENA_BILLING_PROVIDER_CONFIG_INVALID,
					message: `billing.catalog price ${id} references unknown product ${productId}.`,
				});
			}
			priceIds.add(id);
			prices.push({
				amount: normalizeBillingMoney(entry.amount),
				id,
				interval: entry.interval,
				metadata: entry.metadata ?? {},
				productId,
				raw: entry,
			});
		}
	}

	return {
		prices: pricesConfigured ? prices : null,
		products: productsConfigured ? products : null,
	};
}
